import { Trans, useLingui } from '@lingui/react/macro';

import { FilterCheckboxItem } from '@/design_system/Filters/FiltersMenu/FilterCheckboxItem';
import { FilterSearch } from '@/design_system/Filters/FiltersMenu/FilterSearch';
import {
  FilterItemNoResults,
  FiltersMenu,
  FiltersSubMenu,
} from '@/design_system/Filters/FiltersMenu/FiltersMenu';
import { FilterTag } from '@/design_system/Filters/FiltersMenu/FilterTag';
import IconAssociate from '@/icons/Associate.svg';

import { useFilters } from '../FiltersState';
import { useGroupFilterState } from '../shared';

const MINIMUM_GROUPS_FOR_SEARCH = 5;

export const FiltersGroup = ({ tag }: { tag?: boolean }) => {
  const { t } = useLingui();

  const {
    groups: { groups, setGroups },
  } = useFilters();

  const { search, setSearch, fetchNextPage, isLoading, entityGroups, count } =
    useGroupFilterState();

  const options = entityGroups.map(({ id, name }) => (
    <FilterCheckboxItem
      key={id}
      checked={groups.some((group) => group.id === id)}
      onChange={(checked) => {
        if (checked) {
          setGroups([...groups, { id, name }]);
        } else {
          setGroups(groups.filter((group) => group.id !== id));
        }
      }}
    >
      {name}
    </FilterCheckboxItem>
  ));

  if (options.length === 0) {
    options.push(<FilterItemNoResults key="empty-desktop" />);
  }

  const searchWithOptions = (
    <FilterSearch
      inputProps={{
        ariaLabel: t({
          id: 'settings.organization.entities.entities.filters.group.search.label',
          message: 'Search groups',
        }),
        placeholder: t({
          id: 'settings.organization.entities.entities.filters.group.search.placeholder',
          message: 'Search groups...',
        }),
        value: search,
        onChange: setSearch,
      }}
      onLoadMore={fetchNextPage}
      isLoading={entityGroups.length === 0 && isLoading}
      isLoadingMore={isLoading}
    >
      {options}
    </FilterSearch>
  );

  const children = count < MINIMUM_GROUPS_FOR_SEARCH ? options : searchWithOptions;

  if (tag) {
    if (groups.length === 0) {
      return null;
    }

    const numberOfGroups = groups.length;

    return (
      <FiltersMenu
        button={
          <FilterTag
            label={t({
              id: 'settings.organization.entities.entities.filters.group.label',
              message: 'Group',
            })}
            value={
              groups.length === 1 ? (
                groups[0].name
              ) : (
                <Trans id="settings.organization.entities.entities.filters.group.multiple">
                  {numberOfGroups} groups
                </Trans>
              )
            }
            tooltip={groups.length > 1 ? groups.map((group) => group.name).join(', ') : undefined}
            onClear={() => setGroups([])}
          />
        }
      >
        {children}
      </FiltersMenu>
    );
  }

  return (
    <FiltersSubMenu
      icon={<IconAssociate />}
      label={t({
        id: 'settings.organization.entities.entities.filters.group.label',
        message: 'Group',
      })}
    >
      {children}
    </FiltersSubMenu>
  );
};
